import type { Invitation } from "@server/types/invitationTypes.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createInvitations as createInvitationsApi,
  deleteInvitation as revokeInvitationApi,
} from "@/api/invitation";
import { useRequiredOrgId } from "@/hooks/useRequiredOrgId";
import { queryKeys } from "@/lib/query-keys";

export const useInvitationActions = () => {
  const queryClient = useQueryClient();
  const organizationId = useRequiredOrgId();
  const invitationsQueryKey = queryKeys.invitations.all(organizationId);

  const createInvitations = useMutation({
    mutationFn: createInvitationsApi,
    onSettled: () => {
      // Refetch invitations after create completes
      void queryClient.invalidateQueries({ queryKey: invitationsQueryKey });
    },
  });

  const revokeInvitation = useMutation({
    mutationFn: revokeInvitationApi,
    onMutate: async ({ invitationId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: invitationsQueryKey,
      });

      // Snapshot the previous value
      const previousInvitations =
        queryClient.getQueryData<Invitation[]>(invitationsQueryKey);

      // Optimistically update by removing the deleted invitation
      if (previousInvitations) {
        queryClient.setQueryData(
          invitationsQueryKey,
          previousInvitations.filter(
            (invitation) => invitation.id !== invitationId,
          ),
        );
      }

      // Return a context with the previous value
      return { previousInvitations };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context from onMutate to roll back
      if (context?.previousInvitations) {
        queryClient.setQueryData(
          invitationsQueryKey,
          context.previousInvitations,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure fresh data
      void queryClient.invalidateQueries({
        queryKey: invitationsQueryKey,
      });
    },
  });

  return { createInvitations, revokeInvitation };
};
